import { useState } from "react";
import { EnumCountry, EnumLanguage, EnumUserType } from "../generated/enums";
import { RequestTypeOf } from "../generated/routes";
import { currentAccountLanguage } from "../i18n/userLanguage";
import { apiFetchRoute } from "./apiFetchRoute";

type CompanyFormData = {
  raisonSociale: string;
  siret: string;
  adresse: string;
  complementAdresse?: string;
  codePostal: string;
  ville: string;
  pays: string;
  telephone: string;
  email: string;
};

type RegistrationFormData = {
  nom: string;
  prenom: string;
  email: string;
  phone_mobile: string;
  phone_fixe: string;
  company: CompanyFormData;
};

export type CreateAccountFormData = {
  type: EnumUserType;
  language: EnumLanguage;
  nom: string;
  prenom: string;
  email: string;
  id_x3: number;
  company: number;
  createNewCompany: boolean;
  company_name: string;
  siret: string;
  address: string;
  address_complementary: string;
  zip_code: string;
  city: string;
  country: EnumCountry;
  phone_mobile: string;
  phone_fixe: string;
  companyEmail: string;
};

type RegistrationResponse = {
  success: boolean;
  error?: string;
  message?: string;
};

export const handleCreateFarmerAccountSubmit = async (
  formData: RegistrationFormData
): Promise<RegistrationResponse> => {
  try {
    // Création de l'entreprise
    // Type du body inféré depuis la route générée
    type CompanyRequest = RequestTypeOf<"postCompanies">;

    const companyBody: CompanyRequest = {
      id_x3: 0,
      company_name: formData.company.raisonSociale,
      siret: formData.company.siret,
      address: formData.company.adresse,
      address_complementary: formData.company.complementAdresse,
      zip_code: formData.company.codePostal,
      city: formData.company.ville,
      country: "FR",
      phone_mobile: formData.company.telephone,
      phone_fixe: formData.company.telephone,
      email: formData.company.email,
    };
    const companyResponse = await apiFetchRoute("postCompanies", companyBody, {
      headers: {
        "Content-type": "application/json",
      },
    });

    if (!companyResponse.ok) {
      const data = await companyResponse.json();
      return {
        success: false,
        error:
          data.message ||
          "Une erreur est survenue lors de la création de l'entreprise",
      };
    }

    const companyData = await companyResponse.json();

    // Création du compte éleveur
    // Type du body inféré depuis la route générée
    type AccountRequest = RequestTypeOf<"postAccountsNoauth">;

    const accountBody: AccountRequest = {
      language: currentAccountLanguage(),
      id_company: companyData.id_company,
      email: formData.email,
      first_name: formData.prenom,
      last_name: formData.nom,
      phone_mobile: formData.phone_mobile,
      phone_fixe: formData.phone_fixe,
    };
    const accountResponse = await apiFetchRoute(
      "postAccountsNoauth",
      accountBody,
      {
        headers: {
          "Content-type": "application/json",
        },
      }
    );

    if (!accountResponse.ok) {
      return {
        success: false,
        error: "Une erreur est survenue lors de la création du compte",
      };
    }

    return {
      success: true,
      message:
        "Votre compte a été créé avec succès ! Un email vous a été envoyé pour créer votre mot de passe et activer votre compte.",
    };
  } catch (error) {
    console.error("Erreur lors de l'inscription:", error);
    return {
      success: false,
      error: "Une erreur est survenue lors de l'inscription",
    };
  }
};

export const useCreateAccount = () => {
  const [companyId, setCompanyId] = useState<number>(-1);

  const createAccount = async (
    formData: CreateAccountFormData
  ): Promise<RegistrationResponse> => {
    try {
      setCompanyId(formData.company);

      if (formData.createNewCompany) {
        // Type du body inféré depuis la route générée
        type CompanyRequest = RequestTypeOf<"postCompanies">;

        const body: CompanyRequest = {
          id_x3: formData.id_x3,
          company_name: formData.company_name,
          siret: formData.siret,
          address: formData.address,
          address_complementary: formData.address_complementary,
          zip_code: formData.zip_code,
          city: formData.city,
          country: formData.country,
          phone_mobile: formData.phone_mobile,
          phone_fixe: formData.phone_fixe,
          email: formData.companyEmail,
        };
        const companyResponse = await apiFetchRoute("postCompanies", body, {
          headers: {
            "Content-type": "application/json",
          },
        });

        if (!companyResponse.ok) {
          return {
            success: false,
            error:
              "Une erreur est survenue lors de la création de l'entreprise",
          };
        }

        const companyData = await companyResponse.json();
        setCompanyId(Number(companyData.id_company));
      }

      // Type du body inféré depuis la route générée
      type AccountRequest = RequestTypeOf<"postAccounts">;

      const body: AccountRequest = {
        type: formData.type,
        language: formData.language,
        id_company: companyId,
        email: formData.email,
        first_name: formData.prenom,
        last_name: formData.nom,
        phone_mobile: formData.phone_mobile,
        phone_fixe: formData.phone_fixe,
      };
      const accountResponse = await apiFetchRoute("postAccounts", body, {
        headers: {
          "Content-type": "application/json",
        },
      });

      if (!accountResponse.ok) {
        return {
          success: false,
          error: "Une erreur est survenue lors de la création du compte",
        };
      }

      return {
        success: true,
        message:
          "L'utilisateur a bien été créé, il recevra un mail de vérification.",
      };
    } catch (error) {
      console.error("Erreur lors de la création du compte:", error);
      return {
        success: false,
        error: "Une erreur est survenue lors de la création du compte",
      };
    }
  };

  return { createAccount, companyId };
};
